import { createApi } from "@reduxjs/toolkit/query/react";
import {
  Faq,
  MyBranding,
  Testimonial,
  WebsiteDetails,
} from "Pages/MyWebiste/types";
import {
  setCroppedAdvisorImage,
  setCroppedImage,
  setCroppedQuoteImage,
  setCroppedYoutubeThumbnail,
  setCustomAgreementTemplates,
  setSelectedAdvisorImage,
  setSelectedImage,
  setSelectedQuoteImage,
  setSelectedYoutubeThumbnail,
  setWebsiteDetails,
} from "store/slices/MyWebsiteSlice";
import { customBaseQuery, handleQueryLifecycle } from "store/utils";

// API response wrapper type based on your API structure
interface ApiResponse<T> {
  data: T;
  message?: string;
  status: number;
}

export interface DomainVerifyResponse {
  available: boolean;
  isVerified: boolean;
  message: string;
}

type Id = string | number;

type MyWebsiteMutationParams = {
  id: Id;
  body: FormData;
  path: string;
  isDelete?: boolean;
  field?: string;
};

export const myWebsiteApi = createApi({
  reducerPath: "myWebsiteApi",
  baseQuery: customBaseQuery,
  tagTypes: ["MyWebsite", "CustomAgreement", "Complaints"],
  endpoints: (builder) => ({
    getMyWebsiteDetails: builder.query<
      ApiResponse<WebsiteDetails>,
      string | number
    >({
      query: (id) => `/company/${id}/website/`,
      providesTags: (_res, _err, id) => [{ type: "MyWebsite", id }],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        dispatch(setWebsiteDetails(data?.data));

        dispatch(
          setCroppedImage(data?.data?.websiteSection?.myBranding?.companyLogo),
        );

        dispatch(
          setCroppedYoutubeThumbnail(
            data?.data?.websiteSection?.aboutMe?.youtubeThumbnail,
          ),
        );
        dispatch(
          setCroppedQuoteImage(
            data?.data?.websiteSection?.quotes?.quoteOwnerUrl,
          ),
        );
        dispatch(
          setCroppedAdvisorImage(
            data?.data?.websiteSection?.heroSection?.advisorPhoto,
          ),
        );

        await dispatch(
          myWebsiteApi.endpoints.getCustomAgreementTemplates.initiate(),
        );
      },
    }),
    updateMyWebsiteDetails: builder.mutation<
      ApiResponse<MyBranding>,
      MyWebsiteMutationParams
    >({
      query: ({ id, body, path, isDelete = false, field = "" }) => {
        const formData = new FormData();

        if (isDelete) {
          formData.append(field, "");
        }

        return {
          url: `/company/${id}/${path}/`,
          method: "PATCH",
          body: isDelete ? formData : body,
        };
      },
      invalidatesTags: (result, error, { id }) =>
        result ? [{ type: "MyWebsite", id }] : [],
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const { data } = await queryFulfilled;

        dispatch(setWebsiteDetails(data?.data));

        dispatch(setCroppedImage(null));
        dispatch(setSelectedImage(null));
        dispatch(setCroppedQuoteImage(null));
        dispatch(setSelectedQuoteImage(null));
        dispatch(setCroppedYoutubeThumbnail(null));
        dispatch(setSelectedYoutubeThumbnail(null));
        dispatch(setCroppedAdvisorImage(null));
        dispatch(setSelectedAdvisorImage(null));
        await handleQueryLifecycle(queryFulfilled);
      },
    }),
    confirmDomain: builder.mutation<DomainVerifyResponse, { data: string }>({
      query: ({ data }) => ({
        url: `/company/check_domain_name/?domain_name=${data}`,
        method: "GET",
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        await handleQueryLifecycle(queryFulfilled);
      },
    }),
    getCustomAgreementTemplates: builder.query<ApiResponse<any>, void>({
      query: () => "/custom-agreement/",
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setCustomAgreementTemplates(data?.data));
        } catch (error) {
          console.error("Error fetching custom agreements:", error);
        }
      },
    }),
    uploadComplaints: builder.mutation<ApiResponse<any>, FormData>({
      query: (formData) => ({
        url: "/upload-complaints/",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    deleteComplianceAuditReport: builder.mutation<any, { company: Id; id: Id }>(
      {
        query: ({ company, id }) => ({
          url: `/company/${company}/delete_compliance_audit/`,
          method: "DELETE",
          body: { id },
        }),
        invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
        onQueryStarted: async (_, { queryFulfilled }) =>
          handleQueryLifecycle(queryFulfilled),
      },
    ),
    createFaqEntry: builder.mutation<any, Partial<Faq>>({
      query: (body) => ({
        url: "/faqs/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    updateFaqEntry: builder.mutation<any, { id: Id; body: Partial<Faq> }>({
      query: ({ id, body }) => ({
        url: `/faqs/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    deleteFaqEntry: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/faqs/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    updateFaqEntries: builder.mutation<any, { body: Faq[] }>({
      query: ({ body }) => ({
        url: `/faqs/update-faqs/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    downloadSampleFile: builder.mutation<ArrayBuffer, any>({
      query: (data) => ({
        url: "/download-sheet/",
        method: "POST",
        body: data,
        responseHandler: (response) => response.arrayBuffer(),
      }),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    createTestimonial: builder.mutation<any, FormData>({
      query: (body) => ({
        url: "/client-testimonial/",
        method: "POST",
        body,
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    updateTestimonial: builder.mutation<any, { id: Id; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/client-testimonial/${id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    deleteTestimonial: builder.mutation<any, Id>({
      query: (id) => ({
        url: `/client-testimonial/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
    updateTestimonials: builder.mutation<any, { body: Testimonial[] }>({
      query: ({ body }) => ({
        url: "/client-testimonial/update-testimonial/",
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result) => (result ? [{ type: "MyWebsite" }] : []),
      onQueryStarted: async (_, { queryFulfilled }) =>
        handleQueryLifecycle(queryFulfilled),
    }),
  }),
});

export const {
  useGetMyWebsiteDetailsQuery,
  useUpdateMyWebsiteDetailsMutation,
  useConfirmDomainMutation,
  useGetCustomAgreementTemplatesQuery,
  useUploadComplaintsMutation,
  useDeleteComplianceAuditReportMutation,
  useCreateFaqEntryMutation,
  useUpdateFaqEntryMutation,
  useDeleteFaqEntryMutation,
  useUpdateFaqEntriesMutation,
  useDownloadSampleFileMutation,
  useCreateTestimonialMutation,
  useUpdateTestimonialMutation,
  useDeleteTestimonialMutation,
  useUpdateTestimonialsMutation,
} = myWebsiteApi;
